import React from "react";
import { Link } from "react-router-dom";
import { Heart, Eye } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useWishlist } from "../context/WishlistContext";
import { useQuickView } from "../context/QuickViewContext";
import { useProductCopy } from "../hooks/useProductCopy";

const MAX_STAGGER_MS = 600;
const STAGGER_STEP_MS = 60;

export const ProductCard = ({ product, index = 0 }) => {
    const { t } = useTranslation();
    const { has, toggle } = useWishlist();
    const { open: openQuickView } = useQuickView();
    const { name } = useProductCopy(product);
    const liked = has(product.id);

    return (
        <article
            data-testid={`product-card-${product.slug}`}
            className="group relative animate-fadeUp"
            style={{ animationDelay: `${Math.min(index * STAGGER_STEP_MS, MAX_STAGGER_MS)}ms` }}
        >
            <Link to={`/product/${product.slug}`} className="block relative overflow-hidden bg-[#141414] border border-white/[0.06] hover:border-white/20 transition-colors">
                <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                        src={product.images?.[0]}
                        alt={name}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    {product.is_new && (
                        <span className="absolute left-3 top-3 bg-[#EDEDE6] px-2 py-1 text-[9px] tracking-[0.22em] uppercase text-[#0C0C0C] font-display">
                            {t("product.new_tag")}
                        </span>
                    )}
                    <div className="absolute bottom-3 left-3 right-3 flex justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                openQuickView(product);
                            }}
                            data-testid={`quick-view-${product.slug}`}
                            className="inline-flex items-center gap-2 bg-[#EDEDE6]/95 px-4 py-2 text-[10px] tracking-[0.22em] uppercase text-[#0C0C0C] font-display hover:bg-white"
                        >
                            <Eye className="h-3 w-3" strokeWidth={1.5} /> {t("shop.quick_view")}
                        </button>
                    </div>
                </div>
            </Link>

            <button
                data-testid={`wishlist-toggle-${product.slug}`}
                onClick={(e) => {
                    e.preventDefault();
                    toggle(product);
                }}
                aria-label="Toggle wishlist"
                className="absolute right-3 top-3 p-2 bg-black/40 backdrop-blur-md border border-white/10 hover:border-white/30 transition-colors"
            >
                <Heart
                    className={`h-3.5 w-3.5 transition-colors ${liked ? "fill-[#F4F4F0] text-[#F4F4F0]" : "text-[#F4F4F0]"}`}
                    strokeWidth={1.4}
                />
            </button>

            <div className="mt-4 flex items-start justify-between gap-3">
                <div>
                    <div className="text-overline text-[#666]">{product.category}</div>
                    <Link to={`/product/${product.slug}`} className="block mt-1.5 text-display text-xl leading-tight hover:opacity-70 transition-opacity">
                        {name}
                    </Link>
                </div>
                <div className="text-brutal text-sm shrink-0 pt-0.5">${product.price}</div>
            </div>
        </article>
    );
};
